import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { GeoPuntoOpcional } from "@/components/common/GeoPuntoOpcional";
import { MoneyInput } from "@/components/common/MoneyInput";
import { api, apiError } from "@/lib/api";

const EMPTY = { nombre: "", direccion: "", monto_solicitado: "", tasa_ofrecida: "", observacion: "" };
const GEO_LEAD_TEXT =
  "Se usa la ubicación actual del dispositivo. Úsala solo si capturas en el domicilio del prospecto.";

// Formulario ÚNICO de captura de lead (5 campos) reutilizado por el módulo Prospección (vendedor)
// y por "Leads de campo" (admin/originación). Usa siempre el mismo endpoint POST /leads.
export function LeadForm({ onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [geoLead, setGeoLead] = useState(null);
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const guardar = async () => {
    if (!form.nombre.trim()) { toast.error("Captura el nombre del prospecto."); return; }
    setSaving(true);
    try {
      await api.post("/leads", {
        nombre: form.nombre, direccion: form.direccion,
        monto_solicitado: form.monto_solicitado ? Number(form.monto_solicitado) : null,
        tasa_ofrecida: form.tasa_ofrecida ? Number(form.tasa_ofrecida) : null,
        observacion: form.observacion,
        geo_lead: geoLead,
      });
      toast.success("Lead capturado.");
      setForm(EMPTY);
      setGeoLead(null);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="mb-1 block text-xs font-medium text-slate-600">Nombre del prospecto *</Label>
        <Input data-testid="lead-nombre" value={form.nombre} onChange={(e) => setF("nombre", e.target.value)} />
      </div>
      <div>
        <Label className="mb-1 block text-xs font-medium text-slate-600">Dirección</Label>
        <Input data-testid="lead-direccion" value={form.direccion} onChange={(e) => setF("direccion", e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mb-1 block text-xs font-medium text-slate-600">Monto solicitado</Label>
          <MoneyInput data-testid="lead-monto" value={form.monto_solicitado} onChange={(v) => setF("monto_solicitado", v)} />
        </div>
        <div>
          <Label className="mb-1 block text-xs font-medium text-slate-600">Tasa ofrecida anual (%)</Label>
          <Input data-testid="lead-tasa" type="number" value={form.tasa_ofrecida} onChange={(e) => setF("tasa_ofrecida", e.target.value)} />
        </div>
      </div>
      <div>
        <Label className="mb-1 block text-xs font-medium text-slate-600">Observación</Label>
        <Textarea data-testid="lead-observacion" rows={3} value={form.observacion} onChange={(e) => setF("observacion", e.target.value)} />
      </div>
      <GeoPuntoOpcional
        testidPrefix="geo-lead"
        label="Marcar ubicación del lead (opcional)"
        microtext={GEO_LEAD_TEXT}
        value={geoLead}
        onCapture={setGeoLead}
        onClear={() => setGeoLead(null)}
      />
      <Button data-testid="lead-guardar" onClick={guardar} disabled={saving} className="w-full gap-2 bg-primary hover:bg-sidebar-hover">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Guardar lead
      </Button>
    </div>
  );
}
